import axios from 'axios';

const rawApiUrl = process.env.REACT_APP_BACKEND_URL || '';
const normalizedApiUrl = rawApiUrl.replace(/\/$/, '');
const apiBaseUrl = normalizedApiUrl ? `${normalizedApiUrl}/api` : '/api';

// Create axios instance
const api = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  googleSession: (sessionId) => api.post('/auth/google/session', { session_id: sessionId }),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
};

// Users API
export const usersApi = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  import: (users) => api.post('/users/import', users),
  getTeam: (managerId) => api.get(`/users/team/${managerId}`),
  export: () => api.get('/users/export', { responseType: 'blob' }),
};

// Team API
export const teamApi = {
  getStatusOverview: (year) => api.get('/team/status-overview', { params: { year } }),
};

// Performance Cycle API
export const performanceCycleApi = {
  get: (year) => api.get(`/performance-cycle/${year}`),
  getAll: () => api.get('/performance-cycles'),
  create: (data) => api.post('/performance-cycle', data),
  update: (year, data) => api.put(`/performance-cycle/${year}`, data),
};

// Departments API
export const departmentsApi = {
  getAll: (includeInactive = false) => api.get('/departments', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
};

// Business Units API
export const businessUnitsApi = {
  getAll: (includeInactive = false) => api.get('/business-units', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/business-units', data),
  update: (id, data) => api.put(`/business-units/${id}`, data),
  delete: (id) => api.delete(`/business-units/${id}`),
};

// Divisions API
export const divisionsApi = {
  getAll: (includeInactive = false) => api.get('/divisions', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/divisions', data),
  update: (id, data) => api.put(`/divisions/${id}`, data),
  delete: (id) => api.delete(`/divisions/${id}`),
};

// Grades API
export const gradesApi = {
  getAll: (includeInactive = false) => api.get('/grades', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/grades', data),
  update: (id, data) => api.put(`/grades/${id}`, data),
  delete: (id) => api.delete(`/grades/${id}`),
};

// Categories API
export const categoriesApi = {
  getAll: (includeInactive = false) => api.get('/categories', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Goals API
export const goalsApi = {
  getAll: (params) => api.get('/goals', { params }),
  getById: (id) => api.get(`/goals/${id}`),
  create: (data) => api.post('/goals', data),
  update: (id, data) => api.put(`/goals/${id}`, data),
  delete: (id) => api.delete(`/goals/${id}`),
  submit: (year) => api.post(`/goals/submit?year=${year}`),
  approve: (id, approved, feedback) => api.post(`/goals/approve/${id}?approved=${approved}${feedback ? `&feedback=${encodeURIComponent(feedback)}` : ''}`),
  getStats: (year) => api.get(`/goals/stats?year=${year}`),
};

// Competencies API
export const competenciesApi = {
  getAll: (activeOnly = true) => api.get('/competencies', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/competencies', data),
  update: (id, data) => api.put(`/competencies/${id}`, data),
  delete: (id) => api.delete(`/competencies/${id}`),
};

// Reviews API
export const reviewsApi = {
  getAll: (params) => api.get('/reviews', { params }),
  getById: (id) => api.get(`/reviews/${id}`),
  create: (data) => api.post('/reviews', data),
  update: (id, data) => api.put(`/reviews/${id}`, data),
  sign: (id, signatureData) => api.post(`/reviews/${id}/sign`, signatureData),
  calibrate: (id, data) => api.post(`/reviews/${id}/calibrate`, data),
  submitEmployeeFeedback: (year, reviewType, feedback, submissionStatus = 'submitted') =>
    api.post(`/reviews/employee-feedback?year=${year}&review_type=${reviewType}`, { feedback, submission_status: submissionStatus }),
  rejectEmployeeFeedback: (employeeId, year, reviewType, feedback) =>
    api.post(`/reviews/employee-feedback/reject?employee_id=${employeeId}&year=${year}&review_type=${reviewType}`, { feedback }),
  submitManagerFeedback: (employeeId, year, reviewType, feedback, ratings = {}, submissionStatus = 'submitted') =>
    api.post(`/reviews/manager-feedback?employee_id=${employeeId}&year=${year}&review_type=${reviewType}`, { feedback, ratings, submission_status: submissionStatus }),
  submitManagerGeneralFeedback: (employeeId, year, reviewType, feedback) =>
    api.post(`/reviews/manager-general-feedback?employee_id=${employeeId}&year=${year}&review_type=${reviewType}`, feedback),
  submitCareerDevelopment: (employeeId, year, notes) =>
    api.post(`/reviews/career-development?employee_id=${employeeId}&year=${year}`, { career_development: notes }),
  submitCompetencyRatings: (employeeId, year, ratings) =>
    api.post(`/reviews/competency-ratings?employee_id=${employeeId}&year=${year}`, { ratings }),
};

// Settings API
export const settingsApi = {
  get: () => api.get('/settings'),
  getPublic: () => api.get('/settings/public'),
  update: (data) => api.put('/settings', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadCeoImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/ceo-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // SMTP Settings
  getSmtp: () => api.get('/settings/smtp'),
  updateSmtp: (data) => api.put('/settings/smtp', data),
  testSmtp: (email) => api.post(`/settings/smtp/test?test_email=${encodeURIComponent(email)}`),
};

// Audit Log API
export const auditLogApi = {
  getAll: (params) => api.get('/audit-logs', { params }),
  getActions: () => api.get('/audit-logs/actions'),
  export: (startDate, endDate) => api.get('/audit-logs/export', { 
    params: { start_date: startDate, end_date: endDate },
    responseType: 'blob' 
  }),
};

// Notifications API
export const notificationsApi = {
  getAll: (params) => api.get('/notifications', { params }),
  sendReminders: (reviewType, daysBefore) => api.post(`/notifications/send-reminders?review_type=${encodeURIComponent(reviewType)}&days_before=${daysBefore}`),
};

// Knowledge Base API
export const knowledgeBaseApi = {
  getAll: () => api.get('/knowledge-base'),
  getAllAdmin: () => api.get('/knowledge-base/all'),
  create: (data) => api.post('/knowledge-base', data),
  update: (id, data) => api.put(`/knowledge-base/${id}`, data),
  delete: (id) => api.delete(`/knowledge-base/${id}`),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getCycleStatus: () => api.get('/dashboard/cycle-status'),
};

// Export API
export const exportApi = {
  exportAll: (year) => api.get(`/export/all?year=${year}`, { responseType: 'blob' }),
  exportEmployee: (employeeId, year) => api.get(`/export/employee/${employeeId}?year=${year}`, { responseType: 'blob' }),
};

// Goal Library APIs
export const goalLibraryTemplatesApi = {
  getAll: () => api.get('/goal-library/templates'),
  getById: (id) => api.get(`/goal-library/templates/${id}`),
  create: (data) => api.post('/goal-library/templates', data),  // { title, is_active }
  update: (id, data) => api.put(`/goal-library/templates/${id}`, data),  // { is_active }
  delete: (id) => api.delete(`/goal-library/templates/${id}`),
};

export const goalLibrarySectionsApi = {
  getAll: (templateId) => api.get(`/goal-library/templates/${templateId}/sections`),
  create: (templateId, data) => api.post(`/goal-library/templates/${templateId}/sections`, data),
  update: (id, data) => api.put(`/goal-library/sections/${id}`, data),
  delete: (id) => api.delete(`/goal-library/sections/${id}`),
};

export const goalLibraryGoalsApi = {
  getAll: (sectionId) => api.get(`/goal-library/sections/${sectionId}/goals`),
  create: (sectionId, data) => api.post(`/goal-library/sections/${sectionId}/goals`, data),
  update: (id, data) => api.put(`/goal-library/goals/${id}`, data),
  delete: (id) => api.delete(`/goal-library/goals/${id}`),
};

export const goalLibraryFunctionsApi = {
  getDistinct: () =>
    api.get('/users', { params: { page_size: 9999 } }).then((r) => {
      const users = r.data?.users || r.data || [];
      const fns = [...new Set(users.map((u) => u.function).filter(Boolean))].sort();
      return { data: fns };
    }),
};

export const goalLibraryApi = {
  getMyTemplates: () => api.get('/goal-library/my-templates'),
  materializeGoals: (year) => api.post('/goal-library/materialize-goals', null, { params: { year } }),
};

// Seed API (for initial setup)
export const seedApi = {
  seed: () => api.post('/seed'),
};

export default api;
